import React from 'react';
import ReactDOM from 'react-dom'
import BaseProps from "../types/BaseProps";
import Modal from "react-bootstrap/Modal";
import {ModalProps} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Icon from "./Icon";

export type DialogProps = BaseProps & Pick<ModalProps, 'size'> & {
  visible?: boolean,
  title?: string | React.ReactElement,
  message: string | React.ReactElement,
  type?: 'confirm' | 'warning'
  onConfirm?: () => void,
  onCancel?: () => void,
};

type DialogState = {
  show: boolean
};

/**
 * Generic dialog component showing confirmation screen.
 */
class Dialog extends React.Component<DialogProps, DialogState> {

  constructor(props: DialogProps) {
    super(props);
    this.state = {
      show: this.props.visible || true
    };
    this.onConfirm = this.onConfirm.bind(this);
    this.onCancel = this.onCancel.bind(this);
  }

  onConfirm() {
    this.props.onConfirm && this.props.onConfirm();
    this.close();
  }

  onCancel() {
    this.props.onCancel && this.props.onCancel();
    this.close();
  }

  close() {
    this.setState({
      show: false
    });
  }

  render() {
    return (
      // Note: animation false is needed to avoid errors about deprecated findDOMNode()
      <Modal show={this.state.show} onHide={this.onCancel} backdrop="static"
             className={this.props.className} animation={false} size={this.props.size}>
        <Modal.Header closeButton>
          <Modal.Title>{this.props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{this.props.message}</Modal.Body>
        <Modal.Footer>
          {
            this.props.type === 'confirm' &&
            <Button variant="secondary"
                    className="DialogCancelButton"
                    onClick={this.onCancel}>
              Cancel
            </Button>
          }
          <Button variant="primary"
                  className="DialogConfirmButton"
                  onClick={this.onConfirm}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

/**
 * Shortcut component for configuring and rendering a confirmation dialog.
 */
export function ConfirmationDialog(props: DialogProps) {
  return (
    <Dialog className="ConfirmationDialog" {...props} type="confirm" title="Confirmation needed"/>
  );
}

/**
 * Shortcut component for configuring and rendering a warning dialog.
 */
export function WarningDialog(props: DialogProps) {
  const title = <><Icon icon="exclamation-triangle" className="mr-2"/>Warning</>;
  return (
    <Dialog className="WarningDialog" {...props} type="warning" title={title}/>
  );
}

/**
 * Placeholder element used by the DialogService.
 */
export function DialogPlaceholder() {
  return <div id="DialogPlaceholder"/>
}

/**
 * Service for rendering dialogs. Instead of using <Dialog /> in components, it allows for
 * programmatic approach of opening and resolving confirmations.
 *
 * Note: The service doesn't support multiple dialogs at once.
 */
export class DialogService {

  private static getPlaceholder() {
    // This placeholder element is needed so React can render and attach listeners.
    const placeholder = document.querySelector('#DialogPlaceholder') as Element;
    // If the element is not cleaned, React won't re-render the Dialog component.
    ReactDOM.unmountComponentAtNode(placeholder);
    return placeholder;
  }

  /**
   * Renders a confirmation dialog with the supplied properties.
   *
   * @param {DialogProps} props - properties for customizing the dialog view and behaviour
   */
  static confirm(props: DialogProps) {
    ReactDOM.render(
      <React.StrictMode>
        <ConfirmationDialog {...props} />
      </React.StrictMode>, DialogService.getPlaceholder());
  }

  /**
   * Renders a warning dialog with the supplied properties
   *
   * @param {DialogProps} props - properties for customizing the dialog view and behaviour
   */
  static warning(props: DialogProps) {
    ReactDOM.render(
      <React.StrictMode>
        <WarningDialog {...props} />
      </React.StrictMode>, DialogService.getPlaceholder());
  }
}
